/* eslint-disable unicorn/filename-case */
import { lazy } from 'react';

import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { SessionProvider } from './services/providers/session-provider.tsx';
import { NuqsAdapter } from 'nuqs/adapters/react';

import { wsService } from '@/services/websocket/websocket-service.ts';

import MainLayout from '@/components/main-layout.tsx';

import ErrorPage from '@/pages/error.page.tsx';
import Dashboard from '@/pages/dashboard/dashboard.page.tsx';
import Plants from '@/pages/plants/plants.page.tsx';
import Plant from '@/pages/plants/[plant]/plant.page.tsx';
import CreatePlant from '@/pages/plants/create-plant/create-plant.page.tsx';
import EditPlant from '@/pages/plants/edit-plant/edit-plant.page.tsx';
import PlantConnectionDetails from '@/pages/plants/[plant]/connection-details/connection-details-plant.page.tsx';
import ControlPanelPlant from '@/pages/plants/[plant]/control-panel/control-panel-plant.page.tsx';
import SchedulePlantPage from '@/pages/plants/[plant]/schedule/schedule-plant.page.tsx';
import BESSPage from '@/pages/bess/[bessId]/bess.page.tsx';
import Devices from '@/pages/devices/devices.page.tsx';
import Device from '@/pages/devices/[device]/device.page.tsx';
import Users from '@/pages/users/users.page.tsx';
import MyProfile from '@/pages/my-profile/my-profile.page.tsx';
import Notifications from '@/pages/notifications/notifications.page.tsx';
import MaintenancePage from '@/pages/maintenance.page.tsx';
import ReleaseNotesPage from '@/pages/release-notes.page.tsx';
import NotFoundPage from '@/pages/not-found.page.tsx';

import ProtectedRoute from './components/protected-route/protected-route.tsx';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/services/i18n/i18n.tsx';
import { PermissionRoute } from '@/components/protected-route/permission-route.tsx';

import { queryClient } from '@/services/api/query-client.ts';
import { usePWAUpdates } from '@/hooks/use-pwa-updates.ts';

import { useMediaQuery } from '@/hooks/use-media-query.tsx';

import AuthenticationLayout from '@/pages/auth/auth-layout.page.tsx';

import { LoginCredentialsForm } from '@/features/auth/components/login/login-credentials-form.tsx';
import { Register } from '@/features/auth/components/register.tsx';
import { ConfirmEmail } from '@/features/auth/components/confirm-email.tsx';
import { ForgotPassword } from '@/features/auth/components/forgot-password.tsx';
import { ResetPassword } from '@/features/auth/components/reset-password.tsx';

import { TwoFactorSetupForm } from '@/features/auth/components/two-factor/two-factor-setup-form.tsx';
import { TwoFactorVerificationForm } from '@/features/auth/components/two-factor/two-factor-verification-form.tsx';
import { TwoFactorResetForm } from '@/features/auth/components/two-factor/two-factor-reset-form.tsx';
import TwoFactorDisableForm from '@/features/auth/components/two-factor/two-factor-disable-form.tsx';
import { PushNotificationsProvider } from '@/services/providers/push-notifications-provider.tsx';
import NotificationRulesPage from '@/pages/notification-rules/notification-rules.page.tsx';
import EventRulesPage from '@/pages/event-rules/event-rules.page.tsx';
import DevicePlant from '@/pages/plants/[plant]/devices/[deviceId]/device-plant.page.tsx';

const ExternalApiPage = lazy(() => import('./pages/docs/external-api/external-api.page.tsx'));

wsService.setQueryClient(queryClient);

// import lazyWithPreload from '@/lib/lazy-with-preload.ts';
// const Authentication = lazy(() => import('./pages/auth'));
// const Overview = lazyWithPreload(() => import('./pages/dashboard'));
//
// const Plants = lazyWithPreload(() => import('./pages/plants'));
// const Plant = lazyWithPreload(() => import('./pages/plants/[plant]'));
// const CreatePlant = lazyWithPreload(() => import('./pages/create-plant'));
// const EditPlant = lazyWithPreload(() => import('./pages/edit-plant'));
// const PlantConnectionDetails = lazyWithPreload(() => import('./pages/plants/[plant]/connection-details'));
// const PlantControlPanel = lazyWithPreload(() => import('./pages/plants/[plant]/control-panel'));
// const Devices = lazyWithPreload(() => import('./pages/devices'));
// const Device = lazyWithPreload(() => import('./pages/devices/[device]'));
// const Users = lazyWithPreload(() => import('./pages/users'));
// const User = lazyWithPreload(() => import('./pages/users/[user]'));

const router = createBrowserRouter(
  [
    {
      path: '*',
      element: <NotFoundPage />,
    },
    {
      path: '/maintenance',
      element: <MaintenancePage />,
    },
    {
      path: '/',
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/',
          element: <AuthenticationLayout />,
          children: [
            {
              index: true,
              element: <LoginCredentialsForm />,
            },
            { path: 'signup', element: <Register /> },
            { path: 'confirm-email', element: <ConfirmEmail /> },
            { path: 'forgot-password', element: <ForgotPassword /> },
            { path: 'reset-password', element: <ResetPassword /> },
            {
              path: 'setup-2fa',
              element: <TwoFactorSetupForm />,
            },
            {
              path: 'verification-2fa',
              element: <TwoFactorVerificationForm />,
            },
            {
              path: 'reset-2fa',
              element: <TwoFactorResetForm />,
            },
            {
              path: 'disable-2fa',
              element: <TwoFactorDisableForm />,
            },
          ],
        },
        {
          element: (
            <SessionProvider>
              <ProtectedRoute />
            </SessionProvider>
          ),
          children: [
            // without layout
            {
              children: [
                {
                  path: 'docs',
                  children: [
                    {
                      path: 'external-api',
                      element: <ExternalApiPage />,
                    },
                  ],
                },
              ],
            },

            // with layout
            {
              element: <MainLayout />,
              children: [
                {
                  path: 'dashboard',
                  element: (
                    <PermissionRoute resource="dashboard" action="view-page.dashboard">
                      <Dashboard />
                    </PermissionRoute>
                  ),
                },
                {
                  path: 'plants',
                  children: [
                    {
                      index: true,
                      element: (
                        <PermissionRoute resource="plants" action="view-page.plants">
                          <Plants />
                        </PermissionRoute>
                      ),
                    },
                    {
                      path: ':plantId',
                      element: (
                        <PermissionRoute resource="plants" action="view-page.plant">
                          <Plant />
                        </PermissionRoute>
                      ),
                    },
                    {
                      path: ':plantId/connection-details',
                      element: (
                        <PermissionRoute
                          resource="plants"
                          action="view-page.connection-details"
                        >
                          <PlantConnectionDetails />
                        </PermissionRoute>
                      ),
                    },
                    {
                      path: ':plantId/control-panel',
                      element: (
                        <PermissionRoute resource="plants" action="view-page.control-panel">
                          <ControlPanelPlant />
                        </PermissionRoute>
                      ),
                    },
                    {
                      path: ':plantId/schedule',
                      element: (
                        <PermissionRoute resource="plants" action="view-page.schedule">
                          <SchedulePlantPage />
                        </PermissionRoute>
                      ),
                    },
                    {
                      path: ':plantId/devices/:deviceId',
                      element: (
                        <PermissionRoute resource="devices" action="view-page.device">
                          <DevicePlant />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'create-plant',
                  children: [
                    {
                      index: true,
                      element: (
                        <PermissionRoute resource="plants" action="view-page.create">
                          <CreatePlant />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'edit-plant',
                  children: [
                    {
                      path: ':plantId',
                      element: (
                        <PermissionRoute resource="plants" action="view-page.edit">
                          <EditPlant />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },

                {
                  path: 'bess/:bessId',
                  children: [
                    {
                      index: true,
                      element: (
                        <PermissionRoute resource="bess" action="view-page.bess">
                          <BESSPage />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },

                {
                  path: 'devices',
                  children: [
                    {
                      index: true,
                      element: (
                        <PermissionRoute resource="devices" action="view-page.devices">
                          <Devices />
                        </PermissionRoute>
                      ),
                    },
                    {
                      path: ':deviceId',
                      element: (
                        <PermissionRoute resource="devices" action="view-page.device">
                          <Device />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'users',
                  children: [
                    {
                      index: true,
                      element: (
                        <PermissionRoute resource="users" action="view-page.users">
                          <Users />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'my-profile',
                  children: [
                    {
                      index: true,
                      element: (
                        <PermissionRoute resource="my-profile" action="view-page.my-profile">
                          <MyProfile />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'notifications',
                  children: [
                    {
                      index: true,
                      element: (
                        <PermissionRoute
                          resource="notifications"
                          action="view-page.notifications"
                        >
                          <Notifications />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'tools',
                  children: [
                    {
                      path: 'event-rules',
                      element: (
                        <PermissionRoute resource="event-rules" action="view-page.event-rules">
                          <EventRulesPage />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'tools',
                  children: [
                    {
                      path: 'notification-rules',
                      element: (
                        <PermissionRoute
                          resource="notification-rules"
                          action="view-page.notification-rules"
                        >
                          <NotificationRulesPage />
                        </PermissionRoute>
                      ),
                    },
                  ],
                },
                {
                  path: 'release-notes',
                  element: <ReleaseNotesPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  { basename: '/' }
);

function App() {
  const isMobile = useMediaQuery('(max-width:640px)');

  usePWAUpdates({
    checkOnLoad: true,
    enablePeriodicChecks: true,
  });

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster
        containerStyle={{ marginRight: '10px' }}
        position={isMobile ? 'top-center' : 'top-right'}
      />
      <I18nextProvider i18n={i18n}>
        <PushNotificationsProvider>
          <NuqsAdapter>
            <RouterProvider router={router} />
          </NuqsAdapter>
        </PushNotificationsProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
